import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import { Apollo } from 'src/gql/generated';
import { agentThreadTypename } from 'src/helpers';
import { RECENT_CHAT_THREADS_VARIABLES } from 'src/hooks/ai-chat';
import { v4 as uuid4 } from 'uuid';

import { $chatThreadState } from '../chat-state';

const AGENT_MESSAGE_FRAGMENT = gql`
  fragment AgentMessageFields on AgentMessage {
    id
    __typename
    role
    status
    text
    data
    feedback
    planState
    createdAt
  }
`;

type AgentThreadMessage = Apollo.GetAgentThreadQuery['agentThread']['messages'][number];

export function useSendUserMessage({
  editorDocumentId,
  onNavigateToThread,
  threadId: currentThreadId,
  visibleContext,
}: {
  editorDocumentId: UUID | null;
  onNavigateToThread: (threadId: UUID) => void;
  threadId: UUID | null;
  visibleContext?: Apollo.AgentVisibleContextInput;
}) {
  const apolloClient = useApolloClient();
  const [sendAgentPrompt] = Apollo.useSendAgentPromptMutation();

  /**
   * Sends a prompt to the agent.
   * Sets the focus to the bottom input after sending the prompt.
   * @param text - The prompt text to send
   * @param savedPromptId - Optional ID of the saved prompt used to compose this message
   */
  return useCallback(
    async (text: string, savedPromptId?: string) => {
      const userMessageId = uuid4();
      const assistantMessageId = uuid4();
      const trimmedText = text.trim();
      const isNewThread = !currentThreadId;
      const threadId = currentThreadId ?? uuid4();
      if (!trimmedText.length) return;

      const createdAt = new Date().toISOString();
      const userMessage: AgentThreadMessage = {
        __typename: 'AgentMessage' as const,
        createdAt,
        data: {},
        feedback: null,
        id: userMessageId,
        planState: null,
        role: Apollo.AgentMessageRole.User,
        status: Apollo.AgentMessageStatus.Complete,
        text: trimmedText,
      };
      const assistantMessage: AgentThreadMessage = {
        __typename: 'AgentMessage' as const,
        createdAt,
        data: { suggestionStates: {} },
        feedback: null,
        id: assistantMessageId,
        planState: null,
        role: Apollo.AgentMessageRole.Assistant,
        status: Apollo.AgentMessageStatus.Streaming,
        text: '',
      };

      if (isNewThread) {
        apolloClient.cache.writeQuery<
          Apollo.GetAgentThreadQuery,
          Apollo.GetAgentThreadQueryVariables
        >({
          data: {
            __typename: 'Query',
            agentThread: {
              __typename: agentThreadTypename,
              artifacts: [],
              context: [],
              id: threadId,
              messages: [userMessage, assistantMessage],
              title: 'Chat',
            },
          },
          query: Apollo.GetAgentThreadDocument,
          variables: { id: threadId },
        });

        const existingThreads =
          apolloClient.readQuery<Apollo.GetAgentThreadsQuery, Apollo.GetAgentThreadsQueryVariables>(
            {
              query: Apollo.GetAgentThreadsDocument,
              variables: RECENT_CHAT_THREADS_VARIABLES,
            }
          )?.agentThreads ?? [];
        apolloClient.writeQuery<Apollo.GetAgentThreadsQuery, Apollo.GetAgentThreadsQueryVariables>({
          data: {
            __typename: 'Query',
            agentThreads: [
              {
                __typename: agentThreadTypename,
                createdAt: new Date().toISOString(),
                id: threadId,
                title: 'New Chat',
              },
              ...existingThreads,
            ],
          },
          query: Apollo.GetAgentThreadsDocument,
          variables: RECENT_CHAT_THREADS_VARIABLES,
        });
      } else {
        const userMessageRef = apolloClient.cache.writeFragment({
          data: userMessage,
          fragment: AGENT_MESSAGE_FRAGMENT,
        });
        const assistantMessageRef = apolloClient.cache.writeFragment({
          data: assistantMessage,
          fragment: AGENT_MESSAGE_FRAGMENT,
        });

        apolloClient.cache.modify({
          fields: {
            messages: (existingMessages = []) => {
              return [...existingMessages, userMessageRef, assistantMessageRef];
            },
          },
          id: apolloClient.cache.identify({ __typename: agentThreadTypename, id: threadId }),
        });
      }
      $chatThreadState.thinkingMessage = {
        messageId: assistantMessageId,
        text: 'Analyzing request...',
      };

      const variables = {
        input: {
          assistantMessageId,
          editorDocumentId,
          prompt: trimmedText,
          savedPromptId,
          threadId,
          userMessageId,
          ...(visibleContext ? { visibleContext } : {}),
        },
      };
      try {
        const { data } = await sendAgentPrompt({ variables });
        if (data && isNewThread) onNavigateToThread(threadId);
      } catch (error) {
        /**
         * The mutation that kicks off the chat-harness workflow has been
         * unguarded — a network blip or 5xx from the resolver used to
         * leave the assistant row in STREAMING with a stuck loading
         * request..." pulse forever. Capture the failure, clear the
         * pulse, and flip the message row to Error so the user sees a
         * recoverable state instead of a frozen UI.
         */
        Sentry.captureException(error, {
          extra: { assistantMessageId, threadId },
          tags: { component: 'chat-harness-send-prompt' },
        });
        $chatThreadState.thinkingMessage = null;
        apolloClient.cache.writeFragment({
          data: {
            __typename: 'AgentMessage' as const,
            id: assistantMessageId,
            status: Apollo.AgentMessageStatus.Error,
          },
          fragment: gql`
            fragment FailedSendAgentMessage on AgentMessage {
              id
              status
            }
          `,
        });
        return;
      }

      // Scroll to the user message after sending the prompt.
      setTimeout(() => {
        const userMessageElement = document.getElementById(userMessageId);
        if (userMessageElement) userMessageElement.scrollIntoView({ behavior: 'smooth' });
      }, 0);
    },
    [
      apolloClient,
      currentThreadId,
      editorDocumentId,
      onNavigateToThread,
      sendAgentPrompt,
      visibleContext,
    ]
  );
}
